import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { PdfViewerComponent } from '@app/components';
import { Applicant } from '@app/models/recruiting/applicant.model';
import { AuthService, FileHelperService, NotifyService, UrlHelperService } from '@app/services';
import { Translatable } from '@app/types/translatable.type';
import { JobAttachmentTypes } from '@applicant-tracker/enums/job-attachment-types.enum';
import { PdfPreviewersStatesInterface } from '@applicant-tracker/interfaces/pdf-previewers-states.interface';
import { ApplicantProfileService, ApplicantService } from '@applicant-tracker/services';
import { ApplicantAttachment } from '@models/recruiting/applicant-attachments.model';
import { JobAttachment } from '@models/recruiting/job-attachments.model';
import { saveAs } from 'file-saver';
import { Subscription } from 'rxjs';

@Component({
    templateUrl: './application.template.html',
    styleUrls: ['./application.style.scss'],
})
export class ApplicationComponent implements OnInit, OnDestroy {
    applicant: Applicant;
    applicantDocument: File | null = null;
    jobAttachmentTypes = JobAttachmentTypes;
    attachmentToDelete: ApplicantAttachment;
    applicantProfileSubscription: Subscription;

    jobAttachments: JobAttachment[] = [];
    uploadAttachments: ApplicantAttachment[] = [];
    applicantAttachments: ApplicantAttachment[] = [];
    allowedFileTypes = ['.pdf', '.csv', '.docx', '.xlsx'];
    pdfPreviewersStates: PdfPreviewersStatesInterface[] = [];

    isLoading = true;
    buttonClick = false;
    hasEmailAutomation = false;
    isApplicantEditModalOpen = false;
    isAddAdditionalDocumentModalOpen = false;
    isApplicantDocumentDeleteModalOpen = false;

    applicantDocumentDisplayName = '';

    maxDocumentAttachments = 5;
    noOfApplicantDocumentsWithoutJobAttachment = 0;

    constructor(
        private notify: NotifyService,
        private auth: AuthService,
        private urlHelper: UrlHelperService,
        private applicantProfileService: ApplicantProfileService,
        public applicantService: ApplicantService,
        public fileHelper: FileHelperService
    ) {}

    async ngOnInit(): Promise<void> {
        this.applicantProfileSubscription = this.applicantProfileService.applicantSubject.subscribe(
            (applicant: Applicant) => {
                this.applicant = applicant;
            }
        );

        try {
            this.applicantAttachments = await this.applicantProfileService.getApplicantAttachments(this.applicant.id);
            this.jobAttachments = await this.applicantProfileService.getJobAttachments(this.applicant.jobPostingId);
        } catch (err) {
            this.isLoading = false;
            this.notify.error(err);
        }

        this.processJobAttachments();

        this.uploadAttachments = this.filterApplicantAttachmentsByType(this.jobAttachmentTypes.File, 'fileId');

        this.noOfApplicantDocumentsWithoutJobAttachment = this.uploadAttachments.filter(
            (attachment) => !attachment.jobAttachmentId
        ).length;

        this.createPDFPreviewersStates();

        this.isLoading = false;
    }

    ngOnDestroy(): void {
        this.applicantProfileSubscription.unsubscribe();
    }

    openApplicantEditModal(): void {
        this.isApplicantEditModalOpen = true;
    }

    showAddAdditionalDocumentModal(): void {
        this.isAddAdditionalDocumentModalOpen = true;
    }

    closeAddAdditionalDocumentModal(): void {
        this.isAddAdditionalDocumentModalOpen = false;
    }

    preventExpansion(): void {
        this.buttonClick = true;
    }

    onCancelApplicantEdit(): void {
        this.applicant.revert();

        this.isApplicantEditModalOpen = false;
    }

    onCancelDeleteApplicantAttachment(): void {
        this.isApplicantDocumentDeleteModalOpen = false;
    }

    onClickDeleteApplicantAttachment(attachmentToDelete: ApplicantAttachment): void {
        this.attachmentToDelete = attachmentToDelete;
        this.isApplicantDocumentDeleteModalOpen = true;
    }

    onExpansion(state: boolean, index: number): void {
        this.pdfPreviewersStates[index].panelState = state;
    }

    onDocumentSelected(files: FileList): void {
        this.applicantDocument = files[0];

        this.applicantDocumentDisplayName =
            this.applicantDocument.name?.substring(0, this.applicantDocument.name.lastIndexOf('.')) || '';
    }

    resetSelectedDocument(): void {
        this.applicantDocument = null;
        this.applicantDocumentDisplayName = '';
    }

    addToApplicantAttachments(jobAttachments: JobAttachment[]): void {
        this.applicantAttachments = [
            ...this.applicantAttachments,
            ...this.convertJobAttachmentsToApplicantAttachments(jobAttachments),
        ];
    }

    // We need to convert JobAttachments as they are only a indication of what files a user can upload but to actually save files we must
    // save them as Applicant Attachments
    convertJobAttachmentsToApplicantAttachments(jobAttachments: JobAttachment[]): ApplicantAttachment[] {
        return jobAttachments.map((jobAttachment: JobAttachment) => {
            return this.createApplicantAttachmentFromJobAttachment(jobAttachment);
        });
    }

    createApplicantAttachmentFromJobAttachment(jobAttachment: JobAttachment): ApplicantAttachment {
        const newApplicantAttachment = new ApplicantAttachment({
            applicantId: this.applicant.id,
            jobAttachmentId: jobAttachment?.id,
            fileId: jobAttachment.fileId ? jobAttachment.fileId : undefined,
            content: jobAttachment.link ? jobAttachment.link : undefined,
        });

        newApplicantAttachment.jobAttachment = jobAttachment;

        return newApplicantAttachment;
    }

    downloadFile(applicantAttachment: ApplicantAttachment): void {
        const path = this.fileHelper.path(applicantAttachment.fileId);

        this.urlHelper.getBlob(path).subscribe((data) => {
            saveAs(data, applicantAttachment.file.name);
        });
    }

    createPDFPreviewersStates(): void {
        this.pdfPreviewersStates = [...new Array(this.uploadAttachments.length).keys()].map((item) => {
            return { panelState: !item, pdfPreviewableError: false, pdfError: false };
        });
    }

    filterApplicantAttachmentsByType(type: JobAttachmentTypes, propName: string): ApplicantAttachment[] {
        return this.applicantAttachments.filter((applicantAttachment: ApplicantAttachment) => {
            return applicantAttachment.jobAttachment?.jobAttachmentTypeId === type || !!applicantAttachment[propName];
        });
    }

    filterByJobAttachments(): JobAttachment[] {
        return this.jobAttachments.filter((jobAttachment: JobAttachment) => {
            return !this.applicantAttachments.find((applicantAttachment: ApplicantAttachment) => {
                return applicantAttachment.jobAttachmentId === jobAttachment.id;
            });
        });
    }

    applicantDocumentToolTip(): Translatable {
        if (this.noOfApplicantDocumentsWithoutJobAttachment < this.maxDocumentAttachments) {
            return 'applicant-tracker.tips.theseDocumentsAreRequestedOnTheJobApplication';
        }

        return 'applicant-tracker.tips.addUpTo5AdditionalDocumentsMessage';
    }

    async saveApplicantDocument(): Promise<void> {
        if (!this.applicantDocument || !this.applicantDocumentDisplayName) {
            return;
        }

        this.closeAddAdditionalDocumentModal();

        this.isLoading = true;

        try {
            const savedDocument = await this.fileHelper.store(this.applicantDocument);

            const newApplicantAttachment = new ApplicantAttachment({
                applicantId: this.applicant.id,
                jobAttachmentId: null,
                fileId: savedDocument.id,
                name: this.applicantDocumentDisplayName,
            });

            await newApplicantAttachment.save();

            this.applicantProfileService.clearApplicantAttachments();

            // Refresh our list of applicant attachment
            await this.loadApplicantAttachments();

            this.notify.success({
                key: 'applicant-tracker.applicants.displayNameWasSuccessfullyAddedToProfile',
                params: { displayName: this.applicantDocumentDisplayName },
            });
        } catch (err) {
            this.notify.error(err.message || 'applicant-tracker.applicants.couldNotSaveDocument');
        } finally {
            this.isLoading = false;
            // Clear the selected document in the ui dialog
            this.resetSelectedDocument();
        }
    }

    async previewDocument(
        applicantAttachment: ApplicantAttachment,
        pdfViewer: PdfViewerComponent,
        index: number
    ): Promise<void> {
        if (!pdfViewer?.status.loading) {
            return;
        }
        this.isLoading = true;

        await this.applicantService.previewDocument(applicantAttachment, pdfViewer, index, this.pdfPreviewersStates);

        this.isLoading = false;
    }

    async onFileUpload(file: File, applicantAttachment: ApplicantAttachment): Promise<void> {
        if (!file) {
            return;
        }

        this.isLoading = true;

        try {
            const savedFile = await this.fileHelper.store(file);
            applicantAttachment.fileId = savedFile.id;
            await applicantAttachment.save();
            this.applicantProfileService.clearApplicantAttachments();
            // Refresh our list of applicant attachment
            await this.loadApplicantAttachments();

            this.notify.success('applicant-tracker.applicants.fileUploaded');
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    async onDeleteApplicantAttachment(): Promise<void> {
        this.isLoading = true;
        this.isApplicantDocumentDeleteModalOpen = false;

        try {
            await this.attachmentToDelete.delete();
            this.applicantProfileService.clearApplicantAttachments();
            await this.loadApplicantAttachments();

            this.notify.success('applicant-tracker.applicants.applicantAttachmentDeleted');
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    async onSave(form: NgForm): Promise<void> {
        if (!this.applicant.isDirty()) {
            this.isApplicantEditModalOpen = false;
            return;
        }

        if (!form.valid) {
            return;
        }

        this.isLoading = true;
        this.isApplicantEditModalOpen = false;

        try {
            await this.applicant.param('company', this.auth.company.id).save();

            this.notify.success('applicant-tracker.applicants.applicantSaved');
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    private async loadApplicantAttachments(): Promise<void> {
        this.applicantAttachments = await this.applicantProfileService.getApplicantAttachments(this.applicant.id);

        this.processJobAttachments();

        this.uploadAttachments = this.filterApplicantAttachmentsByType(this.jobAttachmentTypes.File, 'fileId');

        this.noOfApplicantDocumentsWithoutJobAttachment = this.uploadAttachments.filter(
            (attachment) => !attachment.jobAttachmentId
        ).length;

        this.pdfPreviewersStates = [...new Array(this.uploadAttachments.length).keys()].map((_) => {
            return { panelState: false, pdfPreviewableError: false, pdfError: false };
        });
    }

    // Process and add any job attachments that are not in the applicant attachment array.
    private processJobAttachments(): void {
        // Filter the jobAttachments based on if a corresponding applicant attachment is found.
        // This means theres a value that exists for the applicant and we should use the applicant attachment.
        const filteredJobAttachments = this.filterByJobAttachments();

        if (filteredJobAttachments.length) {
            this.addToApplicantAttachments(filteredJobAttachments);
        }
    }
}
